/**
 * Petal Fall Effect (Teacher's Day, etc.)
 * Very slow floating flower petals – respect, gratitude. Minimal distraction.
 */

import { useEffect, useRef } from 'react'

interface PetalFallProps {
  intensity?: 'low' | 'medium'
}

const PETAL_COLORS = [
  'rgba(255, 182, 193,', // pink
  'rgba(255, 218, 185,', // peach
  'rgba(230, 230, 250,', // lavender
  'rgba(255, 228, 196,', // bisque
]

interface Petal {
  x: number
  y: number
  w: number
  h: number
  color: string
  rotation: number
  rotationSpeed: number
  speedY: number
  speedX: number
  alpha: number
}

export function PetalFall({ intensity = 'low' }: PetalFallProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const petalsRef = useRef<Petal[]>([])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const count = intensity === 'low' ? 8 : 14

    const init = () => {
      petalsRef.current = []
      for (let i = 0; i < count; i++) {
        petalsRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          w: 6 + Math.random() * 8,
          h: 10 + Math.random() * 10,
          color: PETAL_COLORS[Math.floor(Math.random() * PETAL_COLORS.length)],
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.02,
          speedY: 0.08 + Math.random() * 0.1,
          speedX: (Math.random() - 0.5) * 0.05,
          alpha: 0.06 + Math.random() * 0.06,
        })
      }
    }

    init()

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      petalsRef.current.forEach((p) => {
        p.y += p.speedY
        p.x += p.speedX
        p.rotation += p.rotationSpeed

        if (p.y > canvas.height + 20) {
          p.y = -20
          p.x = Math.random() * canvas.width
        }
        if (p.x < -20) p.x = canvas.width + 20
        if (p.x > canvas.width + 20) p.x = -20

        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rotation)
        ctx.fillStyle = `${p.color} ${p.alpha})`
        ctx.beginPath()
        ctx.ellipse(0, 0, p.w / 2, p.h / 2, 0, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      })

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  )
}
